import { useTheme } from '../../hooks/useTheme';
import { useCartStore } from '../../store/cart.store';
import { openWhatsAppCart } from '../../utils/whatsapp';
import { useNavigate } from 'react-router-dom';
import { 
  X, 
  Plus, 
  Minus, 
  Trash2, 
  ShoppingBag,
  Truck,
  ArrowRight,
  Check,
  Package
} from 'lucide-react';
import { useEffect, useState } from 'react';

export const CartDrawer = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { 
    items, 
    isOpen, 
    closeCart, 
    updateQuantity, 
    removeItem, 
    getTotalPrice, 
    getSavings,
    getTotalItems,
    clearCart
  } = useCartStore();

  const [isVisible, setIsVisible] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [lastItemCount, setLastItemCount] = useState(0);

  const handleCheckout = () => {
    closeCart();
    const isAuth = !!localStorage.getItem('accessToken');
    navigate(isAuth ? '/cliente/checkout' : '/checkout');
  };

  const totalItems = getTotalItems();
  const totalPrice = getTotalPrice();
  const savings = getSavings();
  const envio = totalPrice >= 50000 ? 0 : 5000;
  const totalFinal = totalPrice + envio;

  // Manejar animación de entrada/salida
  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      // Si se agregó un nuevo item, mostrar mensaje de éxito
      if (totalItems > lastItemCount && lastItemCount > 0) {
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 2000);
      }
      setLastItemCount(totalItems);
    } else {
      const timer = setTimeout(() => setIsVisible(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen, totalItems, lastItemCount]);

  if (!isVisible && !isOpen) return null;

  return (
    <>
      {/* Drawer - Posicionado debajo del header */}
      <div 
        className={`fixed right-0 top-24 md:top-[120px] z-40 h-[calc(100vh-6rem)] md:h-[calc(100vh-120px)] w-full max-w-[420px] shadow-2xl transform transition-transform duration-300 ease-out border-l ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        } ${theme === 'dark' ? 'bg-gray-900/95 backdrop-blur-sm border-gray-700' : 'bg-white/95 backdrop-blur-sm border-gray-200'}`}
      >
        {/* Header del carrito */}
        <div className={`flex items-center justify-between p-4 border-b ${
          theme === 'dark' ? 'border-gray-800' : 'border-gray-200'
        }`}>
          <div className="flex items-center space-x-3">
            <ShoppingBag className="w-5 h-5 text-[#2196F3]" />
            <h2 className={`text-lg font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              Tu Carrito
            </h2>
            {totalItems > 0 && (
              <span className="px-2 py-0.5 bg-[#4CAF50]/10 dark:bg-[#4CAF50]/20 text-[#0d5c3b] dark:text-[#4CAF50] text-xs font-medium rounded-full">
                {totalItems}
              </span>
            )}
          </div>
          <button
            onClick={closeCart}
            className={`p-1.5 rounded-lg transition-colors ${
              theme === 'dark' 
                ? 'text-gray-400 hover:text-white hover:bg-gray-800' 
                : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
            }`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Success Message */}
        {showSuccess && (
          <div className={`mx-4 mt-3 p-2 rounded-lg flex items-center space-x-2 animate-fade-in ${
            theme === 'dark' ? 'bg-[#4CAF50]/20 border border-[#4CAF50]/30' : 'bg-[#4CAF50]/10 border border-[#4CAF50]/20'
          }`}>
            <div className="w-5 h-5 bg-[#4CAF50] rounded-full flex items-center justify-center">
              <Check className="w-3 h-3 text-white" />
            </div>
            <span className={`text-xs font-medium ${theme === 'dark' ? 'text-[#4CAF50]' : 'text-[#0d5c3b]'}`}>
              Producto agregado
            </span>
          </div>
        )}

        {/* Content */}
        {items.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-6" style={{ height: 'calc(100% - 180px)' }}>
            <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-3">
              <ShoppingBag className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className={`text-lg font-semibold mb-1 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              Carrito vacío
            </h3>
            <p className={`text-center text-sm mb-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
              Agrega productos desde la página
            </p>
            <button
              onClick={closeCart}
              className="px-4 py-2 bg-gradient-to-r from-[#4CAF50] to-[#2196F3] hover:opacity-90 text-white rounded-lg text-sm font-medium transition-all"
            >
              Ver Productos
            </button>
          </div>
        ) : (
          <>
            {/* Items */}
            <div className="overflow-y-auto p-4 space-y-3" style={{ height: 'calc(100% - 240px)' }}>
              {items.map((item) => (
                <div 
                  key={item.id}
                  className={`flex items-center space-x-3 p-3 rounded-xl transition-all hover:shadow-md ${
                    theme === 'dark' ? 'bg-gray-800' : 'bg-gray-50'
                  }`}
                >
                  {/* Imagen */}
                  <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                    {item.imagen && (item.imagen.startsWith('http') || item.imagen.startsWith('/')) ? (
                      <img src={item.imagen} alt={item.nombre} className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-2xl">{item.imagen || '📦'}</span>
                    )}
                  </div>
                  
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <h4 className={`font-medium text-sm truncate ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      {item.nombre}
                    </h4>
                    {item.presentacionNombre && (
                      <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                        {item.presentacionNombre}
                      </p>
                    )}
                    <div className="flex items-center space-x-1 mt-0.5">
                      <span className="text-[#2196F3] font-semibold text-sm">
                        ${item.precio.toLocaleString()}
                      </span>
                      {item.precioAntes && (
                        <span className={`text-xs line-through ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>
                          ${item.precioAntes.toLocaleString()}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Cantidad */}
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={() => updateQuantity(item.id, item.cantidad - 1, item.presentacionId)}
                      className={`p-1 rounded transition-colors ${
                        theme === 'dark'
                          ? 'bg-gray-700 hover:bg-gray-600 text-white'
                          : 'bg-white hover:bg-gray-200 text-gray-700'
                      }`}
                    >
                      <Minus className="w-3 h-3" />
                    </button>
                    <span className={`w-6 text-center text-sm font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      {item.cantidad}
                    </span>
                    <button
                      onClick={() => updateQuantity(item.id, item.cantidad + 1, item.presentacionId)}
                      className={`p-1 rounded transition-colors ${
                        theme === 'dark'
                          ? 'bg-gray-700 hover:bg-gray-600 text-white'
                          : 'bg-white hover:bg-gray-200 text-gray-700'
                      }`}
                    >
                      <Plus className="w-3 h-3" />
                    </button>
                  </div>

                  {/* Eliminar */}
                  <button
                    onClick={() => removeItem(item.id, item.presentacionId)}
                    className="p-1 text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>

            {/* Footer */}
            <div className={`absolute bottom-0 left-0 right-0 border-t p-4 space-y-3 bg-inherit ${
              theme === 'dark' ? 'border-gray-800' : 'border-gray-200'
            }`}>
              {/* Resumen */}
              <div className={`space-y-1 text-xs ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                <div className="flex justify-between">
                  <span>Subtotal ({totalItems})</span>
                  <span>${totalPrice.toLocaleString()}</span>
                </div>
                {savings > 0 && (
                  <div className="flex justify-between text-[#4CAF50]">
                    <span>Ahorro</span>
                    <span>-${savings.toLocaleString()}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="flex items-center">
                    <Truck className="w-3 h-3 mr-1" />
                    Envío
                  </span>
                  <span className={envio === 0 ? 'text-[#4CAF50] font-medium' : ''}>
                    {envio === 0 ? 'GRATIS' : `$${envio.toLocaleString()}`}
                  </span>
                </div>
                {envio > 0 && (
                  <p className={`text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>
                    Faltan ${(50000 - totalPrice).toLocaleString()} para envío gratis
                  </p>
                )}
              </div>

              {/* Total */}
              <div className={`pt-2 border-t flex justify-between items-center ${
                theme === 'dark' ? 'border-gray-800' : 'border-gray-200'
              }`}>
                <span className={`font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  Total
                </span>
                <span className="text-xl font-bold text-[#2196F3]">
                  ${totalFinal.toLocaleString()}
                </span>
              </div>

              {/* Botones */}
              <div className="space-y-2">
                {false && (
                  <button 
                    onClick={handleCheckout}
                    className="w-full py-3 bg-gradient-to-r from-[#4CAF50] to-[#2196F3] hover:opacity-90 text-white rounded-xl font-bold transition-all flex items-center justify-center space-x-2 text-sm"
                  >
                    <span>Finalizar Compra</span>
                    <ArrowRight className="w-4 h-4" />
                  </button>
                )}
                <button 
                  onClick={() => openWhatsAppCart(items)}
                  className="w-full py-3 bg-[#25D366] hover:bg-[#128C7E] text-white rounded-xl font-bold transition-all flex items-center justify-center space-x-2 text-sm"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.567-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.50 3.488"/>
                  </svg>
                  <span>Pedir por WhatsApp</span>
                </button>
                <div className="flex space-x-2">
                  <button
                    onClick={closeCart}
                    className={`flex-1 py-2 rounded-lg font-medium transition-colors text-xs flex items-center justify-center space-x-1 ${
                      theme === 'dark'
                        ? 'text-[#4CAF50] hover:text-[#4CAF50]/80 bg-gray-800 hover:bg-gray-700'
                        : 'text-[#0d5c3b] hover:text-[#2196F3] bg-gray-100 hover:bg-gray-200'
                    }`}
                  >
                    <Package className="w-3 h-3" />
                    <span>Seguir comprando</span>
                  </button>
                  <button
                    onClick={() => { clearCart(); closeCart(); }}
                    className={`px-3 py-2 rounded-lg font-medium transition-colors text-xs ${
                      theme === 'dark'
                        ? 'text-gray-400 hover:text-white hover:bg-gray-800'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    Vaciar
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

    </>
  );
};

export default CartDrawer;
